import type { Locale } from "./i18n";

import { svgFrom } from "./glyphs";
import { setLanguage, t } from "./i18n";
import screenshotBarCss from "./screenshot-bar.css?inline";
import { attachStyles } from "./styles";

const HOST_ID = "weloop-screenshot-bar";

export interface ScreenshotBarHandle {
	readonly dispose: () => void;
	// Take the bar out of the page while the frame is grabbed, so the bar
	// itself never appears on the screenshot. Reversible for a dismissed
	// picker, when the user gets the bar back to try again.
	readonly setHidden: (hidden: boolean) => void;
}

// Camera glyph, inline so the bundle stays icon-font free.
const CAMERA_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';

/**
 * Floating bottom-of-viewport bar shown on the host page while the widget
 * waits for a screenshot. The widget iframe is closed at that point so the
 * user can bring the screen they want to show into view – on this page or
 * after navigating to another one – then press the big button to capture it.
 *
 * Same closed-shadow-root pattern and stacking level as the recording bar.
 */
export const mountScreenshotBar = (
	locale: Locale,
	handlers: {
		readonly onCancel: () => void;
		readonly onTake: () => void;
	},
): ScreenshotBarHandle => {
	const host = document.createElement("div");
	host.id = HOST_ID;
	setLanguage(host, locale);
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, screenshotBarCss);

	const bar = document.createElement("div");
	bar.className = "bar";
	bar.setAttribute("role", "group");
	bar.setAttribute("aria-label", t(locale, "screenshot_take"));

	const hint = document.createElement("span");
	hint.className = "hint";
	hint.textContent = t(locale, "screenshot_hint");

	const take = document.createElement("button");
	take.className = "take";
	take.type = "button";
	take.append(
		svgFrom(CAMERA_SVG),
		document.createTextNode(t(locale, "screenshot_take")),
	);
	take.addEventListener("click", handlers.onTake);

	const cancel = document.createElement("button");
	cancel.className = "cancel";
	cancel.type = "button";
	cancel.textContent = t(locale, "screenshot_cancel");
	cancel.addEventListener("click", handlers.onCancel);

	bar.append(hint, take, cancel);
	shadow.append(bar);
	document.body.append(host);

	// The open panel holds focus inside the widget frame. WebKit keeps it
	// there once the frame is hidden, so a key press never reaches this page
	// and Escape could not cancel. Focusing the main button pulls focus back
	// to the host page and puts keyboard users on the next action.
	const focusTake = (): void => {
		take.focus({ preventScroll: true });
	};
	focusTake();

	// Escape cancels, the same way it closes the panel. Ignored while the bar
	// is hidden for the capture itself.
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- KeyboardEvent is an external mutable DOM interface
	const handleKey = (event: KeyboardEvent): void => {
		if (event.key === "Escape" && !host.hasAttribute("hidden")) {
			handlers.onCancel();
		}
	};
	document.addEventListener("keydown", handleKey);

	return {
		dispose: () => {
			take.removeEventListener("click", handlers.onTake);
			cancel.removeEventListener("click", handlers.onCancel);
			document.removeEventListener("keydown", handleKey);
			host.remove();
		},
		setHidden: (hidden) => {
			host.hidden = hidden;
			if (!hidden) {
				focusTake();
			}
		},
	};
};
